// Type contracts for Henry's api_v2/item/main_resource/* endpoints.
// Source: feature/migrate_mc_question (commit 053a5000 + 884b5f4a + bb674615 +
// 9bd18b19 + 086faadd from 2026-05-27) of bitbucket.org/evi-devops/evi-resource-service.
//
// Companion docs: vault/AIQBP - QB Platform/Henry v2 - Required Backend Amendments.md
// + Henry v2 Reconciliation.md.

/** Standard envelope from Henry's v2 controllers. `result === 1` is the only
 *  success value; everything else is a controlled failure (0/-1/2/3/4). */
export interface MainResourceEnvelope<T> {
  result: number;
  /** Henry's controllers wrap the payload under `data` (verified via
   *  uat-resource.evischool.com.cn 2026-05-28: e.g.
   *  `{result:1, data:{total, rows}, checksum:"..."}`). Some Postman docs
   *  show `ret`; treat `data` as canonical and accept `ret` as fallback. */
  data?: T;
  ret?: T;
  checksum?: string;
}

/** Result code semantics shipped on the v2 surface. Keep in sync with the
 *  PHP constants in `evi-resource-service` (Api_Auth_Controller). */
export enum MainResourceResultCode {
  Successful = 1,
  Failed = 0,
  ActionUnsuccessful = -1,
  /** API_RESULT_ACTION_UNSUCCESSFUL from application/config/api.php — a DB write
   *  inside a transaction failed and rolled back (e.g. save_all). Same user-facing
   *  semantics as Failed=0: nothing was persisted, retry. */
  DbWriteFailed = 106,
  NotFound = 2,
  UserNotPermitted = 3,
  SchoolNotPermitted = 4,
  /** Resource-scoped permission codes from api/config/api.php. Distinct from
   *  the user-scope 3/4 — these come out of resource-access checks inside
   *  Main_resource_service::getFullInfo. Henry returns 1402 when the caller
   *  isn't allowed to view a resource's editorial detail, then falls back
   *  to the legacy iframe response shape {name, res_type, iframe_url}. */
  ResourceUserNotPermitted = 1402,
  ResourceSchoolNotPermitted = 1403
}

export function describeResultCode(code: number): string {
  switch (code) {
    case MainResourceResultCode.Successful:
      return 'OK';
    case MainResourceResultCode.NotFound:
      return '找不到此資源';
    case MainResourceResultCode.UserNotPermitted:
    case MainResourceResultCode.ResourceUserNotPermitted:
      return '您沒有權限存取此題目（Henry 後端 RESOURCE_USER_NOT_PERMITTED）';
    case MainResourceResultCode.SchoolNotPermitted:
    case MainResourceResultCode.ResourceSchoolNotPermitted:
      return '此題目不屬於目前學校';
    case MainResourceResultCode.ActionUnsuccessful:
      return '操作未成功';
    case MainResourceResultCode.Failed:
    case MainResourceResultCode.DbWriteFailed:
      return '資料庫寫入失敗，未儲存（請重試）';
    default:
      return `後端錯誤碼 ${code}`;
  }
}

function formatMainResourceErrorMessage(code: number, ret: unknown): string {
  const detail =
    typeof ret === 'string' ? ret : ret && typeof ret === 'object' ? JSON.stringify(ret) : '';
  return `main_resource request failed: result=${code}${detail ? ` (${detail})` : ''}`;
}

export class MainResourceError extends Error {
  constructor(public readonly code: number, public readonly ret: unknown) {
    super(formatMainResourceErrorMessage(code, ret));
    this.name = 'MainResourceError';
  }
}

// ── Asset language enums ─────────────────────────────────────────────────

/** Wire values accepted by `oss_signurl_image/background/answer` for the
 *  visual-language axis. Henry stores these literal strings. */
export type Lang = 'zh' | 'en' | 'cn';

/** Wire values accepted by `oss_signurl_sound/video/answer_sound` for the
 *  voice-over-language axis on WRITE. Read responses use numeric strings;
 *  see VoLangCode below. */
export type VoLang = 'cantonese' | 'putonghua' | 'english';

/** Numeric form returned by `get_full_info` on the read side. 1 = Cantonese,
 *  2 = Putonghua, 3 = English. Use voLangFromCode to normalise. */
export type VoLangCode = '1' | '2' | '3';

export function voLangToCode(value: VoLang): VoLangCode {
  switch (value) {
    case 'cantonese':
      return '1';
    case 'putonghua':
      return '2';
    case 'english':
      return '3';
  }
}

export function voLangFromCode(code: VoLangCode | string | null | undefined): VoLang {
  switch (String(code)) {
    case '2':
      return 'putonghua';
    case '3':
      return 'english';
    case '1':
    default:
      return 'cantonese';
  }
}

// ── List endpoint (get_editorial_list, shipped 9bd18b19) ─────────────────

export interface EditorialListFilters {
  level?: 'k1' | 'k2' | 'k3';
  subject?: string;
  scope?: string;
  unit?: string;
  category?: string;
  // topic_type_id (the 4th-level leaf of the taxonomy). The panel resolves
  // the user-picked (subject, scope, unit, topic) cascade to a numeric id
  // before sending; Henry's get_editorial_list accepts either category
  // (string) or topic_id (number) — prefer topic_id when the cascade is
  // fully selected, since it's the canonical leaf handle.
  topic_id?: number;
  // Henry filters template by the numeric question_type_id (1-11), not T1-T11.
  template?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  publish_status?: 'DRAFT' | 'PUBLISHED';
  /** ⚠ Currently a NO-OP server-side. Henry removed `editorial_status` from
   *  get_editorial_list's filter map in `c0318cb3` ("remove unused filter input
   *  param", 2026-07-28) — but the panel does send it, so it was not unused.
   *  Pending a backend revert; the column is still returned on each row. */
  editorial_status?: EditorialStatus;
  /** Which resource types to include. Backend defaults to 'mc_math' when
   *  omitted, hiding Chinese rows — the bank list must send 'all'. */
  res_type?: 'mc_math' | 'mc_chinese' | 'all';
  keyword?: string;
}

export interface ListControl {
  limit?: number;
  offset?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

export interface EditorialListResponse {
  total: number;
  rows: EditorialListRow[];
}

export interface EditorialListRow {
  /** Renamed from `id` to `item_id` in d7ac2d52. Resource_item.id. */
  item_id: number;
  res_id: number;
  adapter_id: number;
  name: string;
  /** Renamed from `publish_status` to `status` in d7ac2d52. */
  status: string;
  editorial_status: EditorialStatus;
  section_meta?: Record<string, unknown> | string | null;
  create_time?: string;
  last_update_time?: string;
  difficulty?: number;
  target_grade?: string;
  question_type_id?: number;
  topic_id?: number;
  topic_name?: string;
  subject?: string;
  learning_domain?: string;
  learning_unit?: string;
}

// ── Editorial workflow (shipped bb674615) ────────────────────────────────

export type EditorialStatus = 'draft' | 'asset_ready' | 'approved' | 'urgent' | 'archived';

// Legal transitions enforced server-side by `_validateEditorialTransition`.
// Keeping it documented client-side too lets the editor disable illegal
// dropdown choices without an extra round-trip.
export const EDITORIAL_TRANSITIONS: Record<EditorialStatus, EditorialStatus[]> = {
  draft: ['asset_ready', 'archived'],
  asset_ready: ['draft', 'approved', 'archived'],
  approved: ['urgent', 'archived'],
  urgent: ['approved', 'archived'],
  archived: ['draft']
};

// ── add_resource (shipped 053a5000 — adds adapter_id to response) ────────

export interface AddResourcePayload {
  /** Backend default is `mc_math`. Chinese word-bank templates (T6–T8) REQUIRE
   *  `mc_chinese` — `addMathQuestion` rejects those templates outright. */
  type?: 'mc_math' | 'mc_chinese';
  /** Optional display name. If omitted, Henry auto-assigns a UUID. */
  name?: string;
  difficulty?: '1' | '2' | '3';
  target_grade?: 'k1' | 'k2' | 'k3';
  question_type_id?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  topic_id?: number;
  question_program?: 'godot' | 'aplus';
  publish_status?: 'DRAFT' | 'PUBLISHED';
}

export interface AddResourceResponse {
  /** Auto-resolved from `course.resource_adapter` via DB trigger on insert.
   *  Stable id for cross-app references (e.g. Godot bridge). This is the
   *  ONLY field Henry's `add_math_question` returns after f970d39a — panel
   *  must call `get_full_info({ id: adapter_id })` immediately to recover
   *  res_id, resource_item_id, and answer_ids. */
  adapter_id: number;
}

// ── save_all (folded answers + §1.5 section_meta + §1.1 editorial_status) ─

export interface SaveAllAnswerLangData {
  lang: Lang;
  name: string;
  image_url?: string;
  image_prompt?: string;
  // Manual uploads still land via oss_callback_answer; generated images are
  // persisted directly through these first-class generator fields.
}

export interface SaveAllAnswer {
  /** From AddResourceResponse.answer_ids[index]. Required on update; omit
   *  to create a fresh answer row (rare). */
  id?: number;
  name: string;
  is_correct: 'Y' | 'N';
  lang_data?: SaveAllAnswerLangData[];
}

export interface SaveAllPayload {
  /** resource.id from add_resource response. */
  res_id: number;
  /** resource_item.id from add_resource response. */
  id: number;
  /** course.resource_adapter.id — the cross-app stable handle returned by
   *  add_math_question and used as the panel's question id. Henry's finalized
   *  save_all on UAT requires this to locate the row; omitting it makes the
   *  save throw (result=0, nothing persisted). Verified 2026-05-29 against the
   *  "Henry Finalized" Postman save_all_new request. */
  adapter_id?: number;
  /** Internal display name. Mirrors the student-facing question text. */
  name: string;
  /** Student-facing question text (full). get_full_info returns it as
   *  FullInfoQuestion.question_name; fromFullInfo reads it back into
   *  questionContent. Without sending it, the question text only persisted via
   *  the 40-char `name` slice (and was clobbered when name held "RES-<id>"). */
  question_name?: string;
  type: 'mc_math' | 'mc_chinese';
  difficulty: '1' | '2' | '3';
  target_grade: 'k1' | 'k2' | 'k3';
  question_type_id: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  topic_id: number;
  question_program: 'godot' | 'aplus';
  publish_status?: 'DRAFT' | 'PUBLISHED';
  /** Authorise destroying a question's Chinese words when flipping
   *  mc_chinese → mc_math. BOTH are required by `save_all`, and
   *  `confirm_chinese_words_purge` must be the exact string 'YES'.
   *  Destructive — only send after an explicit editor confirmation.
   *  (Replaces the never-read `confirm_type_change`/`confirm_type_value`.) */
  purge_chinese_words?: 1;
  confirm_chinese_words_purge?: 'YES';
  /** §1.1. Validated against EDITORIAL_TRANSITIONS server-side; rejection
   *  surfaces as MainResourceResultCode.ActionUnsuccessful (-1). */
  editorial_status?: EditorialStatus;
  /** §1.5. Henry accepts either object or pre-stringified JSON. */
  section_meta?: Record<string, unknown> | string;
  char_id?: string | number;
  generated_draft_question_id?: string;
  generated_template_id?: string;
  generated_option_type?: string;
  generated_option_layout?: string;
  generated_explanation?: string;
  generated_image_url?: string;
  answers: SaveAllAnswer[];
}

/**
 * `save_all` response (`Main_resource_editorial_service::saveAll`).
 *
 * For `type=mc_chinese`, when no valid word-audio pin exists and `char_id` +
 * `chinese_word_char_map` + char-audio OSS clips are ready, the backend
 * IMM-combines (or copies a single clip) into `resource_chinese_word_audio`
 * and returns the new id. Soft-fail (empty map / missing clips / IMM error)
 * yields `chinese_word_audio_id: null` plus optional
 * `chinese_word_audio_combine_reason` — save still succeeds editorially.
 */
export interface SaveAllResponse {
  adapter_id?: number;
  res_id?: number;
  item_id?: number;
  editorial_status?: string;
  status?: string;
  answer_type?: string | null;
  /** resource_chinese_word_audio.id when combine/pin resolved; null on soft-fail. */
  chinese_word_audio_id?: number | null;
  /** Present when mc_chinese combine soft-failed (e.g. EMPTY_CHAR_MAP). */
  chinese_word_audio_combine_reason?: string | null;
  type_changed?: boolean;
  type_old?: string;
  type_new?: string;
}

// ── get_full_info read shape ─────────────────────────────────────────────
//
// Verified live 2026-05-28 against uat-resource. The real payload is:
//   data: {
//     item: FullInfoQuestion[]   // single canonical question list;
//                               // row carries item_type === 'mc_question'
//                               // for the MC question type marker
//   }
// Each question carries nested answer[], setting[], question_image[],
// background[], video[], sound[]. String ids throughout. Asset `file`
// fields are FULL OSS URLs (https://...aliyuncs.com/...), not bare
// filenames. Answer text lives in answer[i].lang[0].name (zh), falling
// back to answer[i].name.
//
// Migration note: prior to the feature/migrate_mc_question merge,
// `mc_question[]` was a duplicate of `item[]` and served as the
// canonical lookup target. The duplicate was dropped on the Henry
// side; `item[0]` is now the only row the panel should read.

export interface FullInfoSetting {
  id: string;
  mc_question_usage: string;
  question_type_id: string;
  topic: string;
  topic_id: string;
  /** k1/k2/k3 from resource_mc_question_setting; drives editor 級別. */
  target_grade?: string;
  difficulty?: string | number;
  /** Question-scoped pin → resource_chinese_word_audio.id (not char-audio). */
  chinese_word_audio_id?: string | number | null;
}

export interface FullInfoImageRow {
  id: string;
  lang: string;
  file: string;
  oss_url?: string | null;
  size: string;
}

export interface FullInfoVoRow {
  id: string;
  /** Numeric string '1'|'2'|'3' = cantonese/putonghua/english on read. */
  vo_lang: string;
  file: string;
  oss_url?: string | null;
  size: string;
}

export interface FullInfoAnswerLang {
  id?: string;
  lang: string;
  /** Per-option image OSS URL (full). null until uploaded. */
  file?: string | null;
  oss_url?: string | null;
  image_url?: string | null;
  size?: string;
  /** Per-lang answer text. null falls back to answer.name. */
  name: string | null;
  image_prompt?: string | null;
}

export interface FullInfoAnswer {
  id: string;
  question_id: string;
  name: string;
  is_correct: 'Y' | 'N';
  answer_type: 'image' | 'normal' | string;
  /** Top-level answer-image OSS URL (full). Empty string until uploaded. */
  file: string;
  oss_url?: string | null;
  image_url?: string | null;
  image_prompt?: string | null;
  size: string;
  lang: FullInfoAnswerLang[];
  lang_data?: FullInfoAnswerLang[];
  sound: FullInfoVoRow[];
}

export interface FullInfoQuestion {
  resource_item_id: string;
  res_id: string;
  display_id: string;
  name: string;
  /** The actual question text shown to students (e.g. "Which fruit is red?"). */
  question_name: string;
  generated_draft_question_id?: string | null;
  generated_template_id?: string | null;
  generated_option_type?: string | null;
  generated_option_layout?: string | null;
  generated_explanation?: string | null;
  generated_image_url?: string | null;
  question_type_id?: string | number | null;
  target_grade?: string;
  difficulty?: string | number;
  topic?: string | null;
  topic_id?: string | number | null;
  topic_type_id?: string | number | null;
  item_type: string;
  editorial_status?: EditorialStatus;
  section_meta?: Record<string, unknown> | string | null;
  full_path: string;
  setting: FullInfoSetting[];
  question_image: FullInfoImageRow[];
  background: FullInfoImageRow[];
  video: FullInfoVoRow[];
  sound: FullInfoVoRow[];
  answer: FullInfoAnswer[];
  mc_chinese?: ChineseWord[];
  chinese_words?: ChineseWord[];
  /** mc_chinese only. Pinned resource_chinese_word_audio row (with oss_url). */
  chinese_word_audio?: ChineseWordAudio | null;
  /** mc_chinese only. Same shape as get_level_qa_full_list chinese_word_char. */
  chinese_word_char?: ChineseTipChar | null;
}

/** chinese_word_char_service::get_with_audios row. Direct readings omit
 *  `sort`; mapped word positions carry it (Godot plays those in order). */
export interface ChineseTipCharAudio {
  id: number;
  jyutping: string;
  vo_lang: number;
  url: string | null;
  is_standard: string;
  ai_status?: string;
  chinese_word_char_id: number;
  sort?: number;
}

export interface ChineseTipChar {
  id: string | number;
  char: string;
  jyutping?: string | null;
  audios: ChineseTipCharAudio[];
}

/** Tip-audio inputs Godot's ChineseTipAudio.select() reads (T7/T8). */
export interface ChineseTip {
  chinese_word_audio: ChineseWordAudio | null;
  chinese_word_char: ChineseTipChar | null;
}

export interface FullInfoResponse {
  item: FullInfoQuestion[];
  topic_type_list?: TopicType[];
  is_schedule?: string;
  publish_status?: string;
  section?: Record<string, unknown> | null;
  mc_chinese?: ChineseWord[];
  chinese_words?: ChineseWord[];
  resource_item?:
    | { mc_chinese?: ChineseWord[]; chinese_words?: ChineseWord[] }
    | Array<{ mc_chinese?: ChineseWord[]; chinese_words?: ChineseWord[] }>;
}

export interface ChineseWordAudio {
  id: string;
  word_id?: string;
  jyutping?: string | null;
  file?: string | null;
  file_name?: string | null;
  oss_url?: string | null;
  oss_path?: string | null;
  status?: string | null;
}

export interface ChineseWord {
  id: string;
  char_id?: string | number | null;
  name?: string | null;
  jyutping?: string | null;
  audios?: ChineseWordAudio[] | null;
  audio?: ChineseWordAudio[] | null;
}

export function topicIdFromRead(value: number[] | number | null | undefined): number | null {
  if (Array.isArray(value)) {
    return value.length ? Number(value[0]) : null;
  }
  if (typeof value === 'number') {
    return value;
  }
  return null;
}

// ── OSS sign-URL endpoints ───────────────────────────────────────────────

export interface OssMeta {
  size: number;
  width?: number;
  height?: number;
  type: string;
}

export interface OssSignUrlMaterialParams {
  /** resource_item.id. Required by image/background/sound/video endpoints. */
  material_id: number;
  file_name: string;
  meta: OssMeta;
  /** For image + background. */
  lang?: Lang;
  /** For sound + video. */
  vo_lang?: VoLang;
}

export interface OssSignUrlAnswerParams {
  /** answer id from get_full_info answer[i].id (after f970d39a, add_math_question
   *  no longer returns answer_ids — must come from a follow-up get_full_info). */
  answer_id: number;
  file_name: string;
  lang: Lang;
  meta: OssMeta;
}

export interface OssCallbackAnswerParams {
  /** resource_mc_answer.id from get_full_info / save_all. Required; missing → result 105. */
  answer_id: number;
  /** Must be `delete` to clear the image. Omitted → upsert. */
  action: 'delete' | 'upsert';
  /** Same lang used on oss_signurl_answer. Default zh. */
  lang?: Lang;
}

export interface OssSignUrlAnswerSoundParams {
  answer_id: number;
  file_name: string;
  vo_lang: VoLang;
  meta: OssMeta;
}

export interface OssSignUrlChineseWordAudioParams {
  res_id: number;
  word_id: number;
  vo_lang: VoLang;
  jyutping: string;
  file_name: string;
  meta: OssMeta;
}

export interface OssSignUrlResponse {
  sign_url: string;
  oss_path: string;
  file_name: string;
  x_oss_callback?: string;
  existing_file?: string;
}

export interface SetChineseWordAudioIdParams {
  resource_item_id: number;
  /** resource_chinese_word_audio.id; null/'' clears the pin so the next save_all can recombine. */
  audio_id: string | number | null;
}

// ── Topic taxonomy (Mc_question_admin_topic_type) ────────────────────────

export interface TopicType {
  topic_type_id: number;
  topic: string;
  subject?: string;
  learning_domain?: string;
  learning_unit?: string;
  topic_grade?: string;
  performance_indicator?: string;
}

/**
 * @deprecated Henry abandoned the hierarchical `get_tree_list` path. The
 * finalized topic API is the flat `api_v2/item/topic_type/*` controller —
 * see `FlatTopicRow` / `FlatTopicListResponse` below. Kept only so the dead
 * `getTreeList` scaffold still compiles; do not use for new code.
 */
export interface TaxonomyTreeNode {
  node_id: number;
  parent_id: number | 'root';
  name: string;
  topic_type_id?: number;
  node_type: 'subject' | 'domain' | 'unit' | 'topic';
  level: number;
}

// ── Finalized flat Topic Type API (api_v2/item/topic_type/*) ─────────────
//
// `get_list` returns the whole taxonomy as a flat adjacency list. Each row is
// one node; `parent_id` null/0/"0" marks a root subject; `level` "1".."4" maps
// subject→learning_domain→learning_unit→topic. For a LEAF (level 4) the `id`
// IS the topic_type_id that save_all stores. Numeric fields arrive as strings.
export interface FlatTopicRow {
  id: string; // PK; for a leaf this is the topic_type_id used by save_all
  parent_id: string | number | null; // null, 0, or "0" → root (subject)
  topic: string; // this node's display name
  subject?: string | null; // denormalized ancestor names (leaf rows only)
  learning_domain?: string | null;
  learning_unit?: string | null;
  topic_grade?: string | null;
  performance_indicator?: string | null;
  level?: string | null; // "1"=subject "2"=domain "3"=unit "4"=topic
  sort?: string | null;
  root_id?: string | null;
  create_time?: string | null;
  create_user?: string | null;
}

// get_list envelope: the finalized "final test" example returns { total, rows };
// an earlier example used { count, list } — both tolerated by the reader.
export interface FlatTopicListResponse {
  total?: number;
  rows?: FlatTopicRow[];
  count?: number;
  list?: FlatTopicRow[];
}
